import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Avatar,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlaceIcon from "@mui/icons-material/Place";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AnalyticsIcon from "@mui/icons-material/Analytics";

// Keyframe animations
const floatingAnimation = `
  @keyframes floatSlow {
    0%, 100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(20px, -20px);
    }
  }
`;

const glowPulse = `
  @keyframes glowPulse {
    0%, 100% {
      opacity: 0.5;
    }
    50% {
      opacity: 0.8;
    }
  }
`;

const FeatureCard = ({ feature, index }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 20;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
    setMousePosition({ x, y });
  };

  return (
    <Card
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setMousePosition({ x: 0, y: 0 });
      }}
      elevation={0}
      sx={{
        height: "100%",
        background: isHovered
          ? "linear-gradient(135deg, rgba(111, 45, 189, 0.15) 0%, rgba(155, 93, 229, 0.1) 100%)"
          : "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        border: "1px solid",
        borderColor: isHovered
          ? "rgba(155, 93, 229, 0.5)"
          : "rgba(255, 255, 255, 0.1)",
        borderRadius: 3,
        transition: "all 0.3s ease",
        transform: isHovered
          ? `perspective(1000px) rotateX(${-mousePosition.y}deg) rotateY(${mousePosition.x}deg) translateY(-8px) scale(1.02)`
          : "perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px) scale(1)",
        boxShadow: isHovered
          ? "0 20px 40px rgba(111, 45, 189, 0.3), 0 0 20px rgba(155, 93, 229, 0.2)"
          : "0 4px 6px rgba(0, 0, 0, 0.3)",
        position: "relative",
        overflow: "hidden",
        "&::before": isHovered
          ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at 50% 50%, rgba(155, 93, 229, 0.1) 0%, transparent 70%)",
              opacity: 1,
              transition: "opacity 0.3s ease",
            }
          : {},
      }}
    >
      <CardContent
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Icon Circle */}
        <Avatar
          sx={{
            width: 64,
            height: 64,
            background: "linear-gradient(135deg, #9B5DE5 0%, #6F2DBD 100%)",
            mb: 2,
            transition: "all 0.3s ease",
            transform: isHovered
              ? "scale(1.1) rotate(5deg)"
              : "scale(1) rotate(0deg)",
            boxShadow: isHovered
              ? "0 8px 16px rgba(155, 93, 229, 0.4)"
              : "none",
          }}
        >
          {feature.icon}
        </Avatar>

        {/* Title */}
        <Typography
          variant="h6"
          sx={{
            mb: 1.5,
            fontWeight: 600,
            color: "white",
            transition: "color 0.3s ease",
          }}
        >
          {feature.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            lineHeight: 1.7,
            flexGrow: 1,
            color: "rgba(255, 255, 255, 0.7)",
          }}
        >
          {feature.description}
        </Typography>
      </CardContent>
    </Card>
  );
};

const Features = () => {
  const features = [
    {
      icon: <ScheduleIcon sx={{ fontSize: 32, color: "white" }} />,
      title: "Smart Scheduling",
      description:
        "Assign shifts and manage schedules with intelligent conflict detection and automatic notifications.",
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 32, color: "white" }} />,
      title: "Live Staff Tracking",
      description:
        "Real-time GPS tracking with geofenced area monitoring. Know exactly where your team is at all times.",
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 32, color: "white" }} />,
      title: "Delay & Overtime Reports",
      description:
        "Automated tracking of late arrivals and overtime hours. Generate detailed reports with one click.",
    },
    {
      icon: <PlaceIcon sx={{ fontSize: 32, color: "white" }} />,
      title: "Area Assignments",
      description:
        "Assign staff to specific locations and track coverage. Ensure all areas are adequately staffed.",
    },
    {
      icon: <EventAvailableIcon sx={{ fontSize: 32, color: "white" }} />,
      title: "Availability Management",
      description:
        "Handle time-off requests, sick leave, and availability updates through a centralized system.",
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 32, color: "white" }} />,
      title: "Analytics Dashboard",
      description:
        "Visualize performance metrics, identify trends, and generate actionable insights to optimize your workforce.",
    },
  ];

  return (
    <Box
      id="features"
      sx={{
        py: { xs: 8, md: 12 },
        background:
          "linear-gradient(180deg, #0a0118 0%, #1a0b2e 50%, #0a0118 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Inject CSS animations */}
      <style>
        {floatingAnimation}
        {glowPulse}
      </style>

      {/* Animated Grid Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `
            linear-gradient(rgba(155, 93, 229, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(155, 93, 229, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
          opacity: 0.6,
          zIndex: 0,
        }}
      />

      {/* Floating Gradient Orbs */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          left: "10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(155, 93, 229, 0.15) 0%, transparent 70%)",
          animation: "floatSlow 8s ease-in-out infinite",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          right: "10%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(111, 45, 189, 0.12) 0%, transparent 70%)",
          animation: "floatSlow 10s ease-in-out infinite",
          animationDelay: "2s",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          right: "20%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(180, 99, 218, 0.1) 0%, transparent 70%)",
          animation: "glowPulse 6s ease-in-out infinite",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              mb: 2,
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "2.5rem" },
              color: "white",
            }}
          >
            Everything You Need to Monitor Your Team
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              maxWidth: 700,
              mx: "auto",
              color: "rgba(255, 255, 255, 0.7)",
            }}
          >
            Powerful features designed for modern workforce management
          </Typography>
        </Box>

        {/* Feature Cards Grid */}
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <FeatureCard feature={feature} index={index} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Features;
